/**
 * Bookstore page (formerly BYO bundle builder).
 *
 * Now a standard product grid:
 *   - Image links to product page (PDP)
 *   - Quick-add overlay (qty +/- + Add to Cart) revealed on hover
 *   - "Added ✓" feedback after successful add, then resets to default
 *   - Filter dropdowns (Grade, Subject, Author) + Sort
 *
 * No bundle state, no discount tier, no panel. The cart drawer is the only
 * cart UI. After a successful add we refresh + open it via window.ChapterHouse.cart.
 */
(function initBookstore() {
  const start = () => {
    const section = document.querySelector('[data-byo-section]');
    if (!section) return;

    const grid = section.querySelector('[data-byo-grid]');
    const cards = grid ? Array.from(grid.querySelectorAll('[data-byo-card]')) : [];
    const sortTrigger = section.querySelector('[data-byo-sort]');
    const emptyState = section.querySelector('[data-byo-empty]');
    const clearBtnVisible = section.querySelector('[data-byo-clear-visible]');

    const gradeOrder = ['kindergarten', '1st-grade', '2nd-grade', '3rd-grade', '4th-grade', '5th-grade', '6th-grade'];

    /* ── Filtering ───────────────────────────────────────────────────── */
    const getCheckedValues = (filterName) => {
      const checkboxes = section.querySelectorAll('[data-byo-filter="' + filterName + '"]:checked');
      return Array.from(checkboxes).map((cb) => cb.value);
    };

    const updateFilterCounts = () => {
      ['grade_level', 'subject', 'author'].forEach((key) => {
        const count = getCheckedValues(key).length;
        const badge = section.querySelector('[data-byo-fdd-count="' + key + '"]');
        if (badge) badge.textContent = count > 0 ? count : '';
      });
      const anyActive = section.querySelector('[data-byo-filter]:checked');
      if (clearBtnVisible) clearBtnVisible.hidden = !anyActive;
    };

    const applyFilters = () => {
      const checkedGrades = getCheckedValues('grade_level');
      const checkedSubjects = getCheckedValues('subject');
      const checkedAuthors = getCheckedValues('author');

      let visibleCount = 0;
      cards.forEach((card) => {
        const cardGrades = card.dataset.gradeLevels ? card.dataset.gradeLevels.split(',') : [];
        const cardSubjects = card.dataset.subjects ? card.dataset.subjects.split(',') : [];
        const cardAuthors = card.dataset.authors ? card.dataset.authors.split(',') : [];

        const passGrade = checkedGrades.length === 0 || checkedGrades.some((g) => cardGrades.includes(g));
        const passSubject = checkedSubjects.length === 0 || checkedSubjects.some((s) => cardSubjects.includes(s));
        const passAuthor = checkedAuthors.length === 0 || checkedAuthors.some((a) => cardAuthors.includes(a));
        const visible = passGrade && passSubject && passAuthor;
        card.hidden = !visible;
        if (visible) visibleCount++;
      });

      if (emptyState) emptyState.hidden = visibleCount > 0;
      updateFilterCounts();
      applySort();
    };

    const clearFilters = () => {
      section.querySelectorAll('[data-byo-filter]:checked').forEach((cb) => { cb.checked = false; });
      applyFilters();
    };

    /* ── Sort (preserves section dividers) ───────────────────────────── */
    const applySort = () => {
      if (!grid) return;
      const selectedSort = sortTrigger ? (sortTrigger.dataset.selectedValue || 'featured') : 'featured';

      const children = Array.from(grid.children);
      const sections = [];
      let current = null;
      children.forEach((el) => {
        if (el.classList.contains('byo__grid-divider')) {
          current = { divider: el, cards: [] };
          sections.push(current);
        } else if (el.matches('[data-byo-card]')) {
          if (current) current.cards.push(el);
        }
      });

      const sorter = (a, b) => {
        switch (selectedSort) {
          case 'az': return a.dataset.title.localeCompare(b.dataset.title);
          case 'za': return b.dataset.title.localeCompare(a.dataset.title);
          case 'grade': {
            const aMin = (a.dataset.gradeLevels || '').split(',').reduce((min, g) => {
              const idx = gradeOrder.indexOf(g);
              return idx !== -1 && idx < min ? idx : min;
            }, gradeOrder.length);
            const bMin = (b.dataset.gradeLevels || '').split(',').reduce((min, g) => {
              const idx = gradeOrder.indexOf(g);
              return idx !== -1 && idx < min ? idx : min;
            }, gradeOrder.length);
            return aMin - bMin;
          }
          case 'subject': {
            const aS = (a.dataset.subjects || '').split(',')[0] || '';
            const bS = (b.dataset.subjects || '').split(',')[0] || '';
            return aS.localeCompare(bS);
          }
          case 'featured':
          default: {
            const aFeat = a.dataset.featured === 'true' ? 0 : 1;
            const bFeat = b.dataset.featured === 'true' ? 0 : 1;
            if (aFeat !== bFeat) return aFeat - bFeat;
            // Sort by lowest grade level so grade bundles render K → 6 by default,
            // and Chapter I/II/III/IV box sets stay in chapter order too.
            const aGrade = (a.dataset.gradeLevels || '').split(',').reduce((min, g) => {
              const idx = gradeOrder.indexOf(g);
              return idx !== -1 && idx < min ? idx : min;
            }, gradeOrder.length);
            const bGrade = (b.dataset.gradeLevels || '').split(',').reduce((min, g) => {
              const idx = gradeOrder.indexOf(g);
              return idx !== -1 && idx < min ? idx : min;
            }, gradeOrder.length);
            if (aGrade !== bGrade) return aGrade - bGrade;
            return a.dataset.title.localeCompare(b.dataset.title);
          }
        }
      };

      const frag = document.createDocumentFragment();
      sections.forEach((sec) => {
        frag.appendChild(sec.divider);
        const visible = sec.cards.filter((c) => !c.hidden).slice().sort(sorter);
        const hidden = sec.cards.filter((c) => c.hidden);
        visible.forEach((c) => frag.appendChild(c));
        hidden.forEach((c) => frag.appendChild(c));
      });
      grid.appendChild(frag);
    };

    /* ── Quick-add to cart ───────────────────────────────────────────── */
    /* Each click adds 1 to cart. The button shows a green pulse animation
       briefly, then increments the small red count badge so the user knows
       how many of this product they've stacked up. The button stays clickable
       so they can rapidly tap to add more. */
    const PULSE_DURATION = 600; // ms — must match CSS keyframe

    const bumpCountBadge = (btn) => {
      const badge = btn.querySelector('[data-byo-cart-count]');
      if (!badge) return;
      const current = parseInt(badge.textContent, 10) || 0;
      badge.textContent = current + 1;
      badge.hidden = false;
    };

    const addToCart = async (card, btn) => {
      const variantId = parseInt(card.dataset.productId, 10);
      if (isNaN(variantId)) { console.warn('[BYO] missing variant id'); return; }

      btn.disabled = true;

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
        });

        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.description || 'Could not add to cart.');
        }

        // Increment the count badge + trigger pulse animation
        bumpCountBadge(btn);
        btn.setAttribute('data-just-added', 'true');

        // Refresh + open the cart drawer
        if (window.ChapterHouse && window.ChapterHouse.cart) {
          if (typeof window.ChapterHouse.cart.refresh === 'function') {
            await window.ChapterHouse.cart.refresh();
          }
          if (typeof window.ChapterHouse.cart.open === 'function') {
            window.ChapterHouse.cart.open();
          }
        }

        // Re-enable button after the pulse animation finishes so the user
        // can immediately click again to add another
        setTimeout(() => {
          btn.removeAttribute('data-just-added');
          btn.disabled = false;
        }, PULSE_DURATION);
      } catch (e) {
        console.error('[BYO] add to cart failed', e);
        alert(e.message || 'Could not add to cart. Please try again.');
        btn.disabled = false;
      }
    };

    /* ── Events ──────────────────────────────────────────────────────── */
    section.addEventListener('click', (event) => {
      // Quick-add to cart (the +cart icon button on each card)
      const addBtn = event.target.closest('[data-byo-add-to-cart]');
      if (addBtn) {
        event.preventDefault();
        event.stopPropagation();
        const card = addBtn.closest('[data-byo-card]');
        if (card) addToCart(card, addBtn);
        return;
      }

      // Clear filters
      if (event.target.closest('[data-byo-clear-filters]')) {
        event.preventDefault();
        clearFilters();
        return;
      }
    });

    // Filter checkbox change events
    section.querySelectorAll('[data-byo-filter]').forEach((cb) => {
      cb.addEventListener('change', applyFilters);
    });

    if (sortTrigger) {
      sortTrigger.addEventListener('change', applySort);
    }

    /* ── Close filter dropdowns on outside click ─────────────────────── */
    document.addEventListener('click', (event) => {
      const clickedInsideFdd = event.target.closest('[data-byo-fdd]');
      section.querySelectorAll('details[data-byo-fdd][open]').forEach((d) => {
        if (d !== clickedInsideFdd) d.removeAttribute('open');
      });
    });

    /* ── Init ────────────────────────────────────────────────────────── */
    applyFilters();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
